import { Button } from "devextreme-react";
import Form, {
  ButtonItem,
  FormRef,
  Item,
  PatternRule,
  RequiredRule,
} from "devextreme-react/form";
import Popup from "devextreme-react/popup";
import notify from "../../lib/notify";
import { QRCodeSVG } from "qrcode.react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { enable2FAAccountLogin, enable2FALogin } from "../../api/settings";
import { ShortenCompanyNames } from "../../types";

type Enable2FAProps = {
  email: string;
  companyId: number;
  onEnabled: () => void;
  onBack: () => void;
};

export const Enable2FA = ({
  email,
  companyId,
  onEnabled,
  onBack,
}: Enable2FAProps) => {
  const { t } = useTranslation();
  const formRef = useRef<FormRef | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [writtenCode, setWrittenCode] = useState("");
  const [userEnteredCode, setUserEnteredCode] = useState("");
  const [codes, setCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const wasSuccessModalVisible = useRef(false);
  const hasMounted = useRef(false);
  const [isSucces, setIsSucces] = useState(false);

  const handleEnable2FA = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await enable2FALogin(email, companyId);

      const issuer = "Invenio HR";
      const sharedKey = response.data?.sharedKey;

      const otpAuthUrl = `otpauth://totp/${issuer}:${email} ${ShortenCompanyNames[companyId]}?secret=${sharedKey}&issuer=${issuer}&digits=6`;
      setQrCode(otpAuthUrl);
      setWrittenCode(sharedKey);
      setIsModalVisible(true);
    } catch (error: any) {
      notify(`${t("Error:")} ${error?.message || t("Unknown error")}`, "error");
    } finally {
      setIsLoading(false);
    }
  }, [t, email, companyId]);

  const handleEnable2FAAccount = useCallback(async () => {
    const validationResult = formRef.current?.instance().validate();
    if (validationResult?.isValid) {
      setIsLoading(true);
      setError(null);
      try {
        const response = await enable2FAAccountLogin(
          email,
          userEnteredCode,
          companyId,
        );
        setCodes(response.data?.recoveryCodes || []);
        setIsModalVisible(false);
        setIsSuccessModalVisible(true);
        if (response.isOk) {
          setIsSucces(true);
        }
      } catch (error: any) {
        notify(
          `${t("Error:")} ${error?.message || t("Unknown error")}`,
          "error",
        );
        setError(t("twoFactorAuth.error"));
      } finally {
        setIsLoading(false);
      }
    }
  }, [t, userEnteredCode, email, companyId]);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    if (!isSuccessModalVisible && isSucces === true) {
      onEnabled();
      setIsSucces(false);
    }
    wasSuccessModalVisible.current = isSuccessModalVisible;
  }, [isSuccessModalVisible, isSucces, onEnabled]);

  return (
    <div className="first-login-form">
      <p className="text-justify">
        {t(
          "This is your first login. To complete the login process, you need to connect your account with two-factor authentication (2FA). For the best experience and increased security, we recommend using the Microsoft Authenticator app on your mobile device.",
        )}
      </p>
      <Button
        onClick={handleEnable2FA}
        disabled={isLoading}
        type="default"
        className="pt-5"
      >
        {t("2fa.Enable 2FA")}
      </Button>
      <Popup
        visible={isModalVisible}
        onHiding={() => setIsModalVisible(false)}
        maxWidth={425}
        height="auto"
        maxHeight="80vh"
        title={t("2fa.Scan the QR code")}
        showCloseButton
        dragEnabled
      >
        <div className="flex flex-col items-center">
          <p className="mt-0 mb-4">{t("2fa.Scan the QR code in your app")}</p>
          <QRCodeSVG value={qrCode ?? ""} size={200} />
          <p className="mt-4 mb-1">{t("2fa.Written code")}:</p>
          <p className="mt-0 mb-4 font-bold">{writtenCode}</p>
          <p className="mt-0">{t("2fa.enterSixDigitCodeDisplayedInApp")}:</p>
          <Form ref={formRef} colCount={4} className="w-full">
            <Item
              dataField="userCode"
              editorOptions={{
                value: userEnteredCode,
                onValueChanged: (e: any) => setUserEnteredCode(e.value),
                maxLength: 6,
                disabled: isLoading,
                placeholder: t("2fa.Code"),
              }}
              label={{
                visible: false,
              }}
              colSpan={3}
            >
              <RequiredRule message={t("2fa.code is required")} />
              <PatternRule
                pattern="^\d{6}$"
                message={t("2fa.codeShouldConsistOfSixDigits")}
              />
            </Item>
            <ButtonItem
              horizontalAlignment="left"
              verticalAlignment="bottom"
              buttonOptions={{
                text: t("common.send"),
                type: "default",
                onClick: handleEnable2FAAccount,
                disabled: isLoading,
                width: "100%",
              }}
            />
          </Form>
        </div>
      </Popup>
      <Popup
        visible={isSuccessModalVisible}
        onHiding={() => setIsSuccessModalVisible(false)}
        maxWidth={500}
        height="auto"
        maxHeight="80vh"
        title={t("2fa.recoveryCodes.header")}
        showCloseButton
        dragEnabled
      >
        <p className="mt-0 mb-4 text-justify">
          {t("2fa.Here are your recovery codes")}
        </p>
        {error && <p className="error-message">{error}</p>}
        {codes.length > 0 ? (
          <ul className="list-none m-0 p-0">
            {codes.map((code, index) => (
              <li key={index}>{code}</li>
            ))}
          </ul>
        ) : (
          <p>{t("2fa.No recovery codes available.")}</p>
        )}
      </Popup>

      <div className="pt-5">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onBack();
          }}
        >
          {t("Back to login")}
        </a>
      </div>
    </div>
  );
};
